import { Button } from "@/components/ui/button";
import { Gavel, Lock } from "lucide-react";
import { ROUND_STATE_LABEL } from "@/lib/disputePlan";

const ROUND_COPY = {
  1: { title: "Validate the Accounts", body: "Ask the credit bureaus and data furnishers to validate the accounts and confirm that the reported information is complete and accurate." },
  2: { title: "Method of Verification", body: "If an account was verified, ask how the investigation was completed and what information was used." },
  3: { title: "Evidence-Based Dispute + CFPB", body: "Use documents, responses, and reporting inconsistencies to prepare an escalated dispute and a CFPB complaint when appropriate." },
};

const LOCK_HINT = {
  2: "Unlocks after a Round 1 response is recorded or 30 days pass after mailing.",
  3: "Unlocks after a Round 2 response is recorded or 30 days pass after mailing.",
};

const PILL = {
  current: "bg-purple-100 text-purple-800", ready: "bg-amber-100 text-amber-900", available: "bg-purple-100 text-purple-800",
  mailed: "bg-sky-100 text-sky-800", waiting: "bg-sky-100 text-sky-800", response: "bg-indigo-100 text-indigo-800",
  completed: "bg-emerald-100 text-emerald-800", not_started: "bg-slate-100 text-slate-600",
};

function RoundCard({ r, isCurrent, cfpbUnlocked, cfpbCount, onCfpb }) {
  const copy = ROUND_COPY[r.round];
  return (
    <li
      className={`relative rounded-3xl border p-5 transition-shadow ${r.locked ? "border-dashed border-slate-300 bg-slate-50 opacity-80" : isCurrent ? "border-purple-300 bg-white shadow-lg ring-2 ring-purple-100" : "border-slate-200 bg-white"}`}
      data-testid={`dashboard-round-${r.round}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Round {r.round}</span>
        {r.locked ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 bg-slate-200 text-slate-600" data-testid={`dashboard-round-${r.round}-status`}>
            <Lock size={12} />Locked
          </span>
        ) : (
          <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${PILL[r.state]}`} data-testid={`dashboard-round-${r.round}-status`}>{ROUND_STATE_LABEL[r.state]}</span>
        )}
      </div>
      <h4 className="mt-3 text-lg font-semibold text-slate-900">{copy.title}</h4>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{copy.body}</p>
      {r.locked && <p className="mt-3 text-xs text-slate-500" data-testid={`dashboard-round-${r.round}-lock-hint`}>{LOCK_HINT[r.round]}</p>}
      {r.round === 3 && cfpbUnlocked && (
        <Button variant="outline" onClick={onCfpb} className="mt-4 w-full sm:w-auto min-h-[44px] rounded-xl border-slate-300" data-testid="dashboard-round-3-cfpb">
          <Gavel size={15} className="mr-2" />{cfpbCount > 0 ? "View CFPB Complaint" : "Prepare CFPB Complaint"}
        </Button>
      )}
    </li>
  );
}

export function RoundRoadmap({ rounds, currentRound, cfpbUnlocked, cfpbCount, onCfpb }) {
  return (
    <section className="mt-10" data-testid="dashboard-roadmap">
      <h3 className="text-lg font-semibold text-slate-900">Your three-round dispute roadmap</h3>
      <ol className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
        {rounds.map((r) => (
          <RoundCard key={r.round} r={r} isCurrent={r.round === (currentRound || 1) && !r.locked && r.state !== "not_started"} cfpbUnlocked={cfpbUnlocked} cfpbCount={cfpbCount} onCfpb={onCfpb} />
        ))}
      </ol>
      <p className="mt-3 text-xs text-slate-500" data-testid="dashboard-roadmap-disclaimer">
        Every dispute is different. Bureaus and furnishers may correct, update, or keep information after investigating, so results aren't guaranteed.
      </p>
    </section>
  );
}
